import { B4, Box, Button, Stack } from "@adfenix/alexandria";
import { ChangeEvent, useState } from "react";
import CampaignStatus from "components/CampaignStatus";
import Card from "components/Card";
import ListingStatus from "components/ListingStatus";
import { ViewResultsButton } from "./ViewResultsButton";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getProgramSelectorUrl } from "utils/getProgramSelectorUrl";
import { PropertyService } from "@adfenix/types";
import CheckMark from "static/svg/check";
import Popover from "components/Popover";
import { useFeatureToggle } from "hooks/useFeatureToggle";
import { useRecoilValue } from "recoil";
import { userSelectedHq } from "atoms/auth";
import { ProgramServiceApi, SellerServiceApi } from "api/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { InviteSellerModal } from "./InviteSellerModal";
import { InviteSellerModalWithoutCDP } from "./InviteSellerModalWithoutCDP";
import imagePlaceholder from "static/image-placeholder.png";

interface Props {
  property: PropertyService.PropertyWithCampaignsSearchModel;
  screenClass: string;
}

const ListingStatusContainer = styled.div`
  position: absolute;
  margin-top: 12px;
`;

const PropertyImage = styled.img`
  width: 100%;
  margin-right: 20px;
  border-radius: 6px;
  object-fit: cover;

  @media (min-width: 768px) {
    height: 106px;
    max-width: 160px;
  }
`;

export const PropertyListing = ({ property, screenClass }: Props) => {
  const [sellerInvited, setSellerInvited] = useState(false);
  const selectedHq = useRecoilValue(userSelectedHq);
  const [inviteSellerModal, setInviteSellerModal] = useState(false);
  const inviteSellerEnabled = useFeatureToggle(
    selectedHq?.id ?? "",
    "invite-seller",
    false
  );

  const { mutate, isLoading } = useMutation(
    () =>
      ProgramServiceApi.inviteSeller.authV1InviteSellerCreate(
        selectedHq?.id ?? "",
        property.id
      ),
    {
      onSuccess: () => {
        setSellerInvited(true);
        setInviteSellerModal(false);
      },
      onError: () => {
        toast.error(t("seller.choice.inviteSeller.error"));
      }
    }
  );

  const showSeeCampaign = property.campaigns.length
    ? property.campaigns.find(
        campaign =>
          campaign.status === "Active" || campaign.status !== "Completed"
      )
    : false;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    seller: { authV1SellerExistsOnPropertyDetail }
  } = SellerServiceApi;
  const { data } = useQuery(
    ["checkIfSellerExistsOnProperty", property.id],
    () => authV1SellerExistsOnPropertyDetail(property.id!)
  );

  return (
    <>
      <Card p="0" key={property.id}>
        <Stack
          spacing={["m", "xs2"]}
          display={["xs", "sm"].includes(screenClass) ? "block" : "flex"}
          alignItems="center"
          justifyContent="space-between"
          p="gutter"
          style={{
            borderBottom: "1px solid #eee"
          }}
        >
          <Box
            display={["xs", "sm"].includes(screenClass) ? "block" : "flex"}
            alignItems="start"
          >
            <ListingStatusContainer>
              <ListingStatus status={property.propertyStatus} t={t} />
            </ListingStatusContainer>

            <PropertyImage
              onError={(event: ChangeEvent<HTMLImageElement>) =>
                event.target.setAttribute("src", imagePlaceholder)
              }
              alt="Property image"
              src={property.mainImageUrl || ""}
            />

            <Stack spacing="s">
              <Box>
                <B4>{property.location.address}</B4>
              </Box>
              <Box display="flex" style={{ gap: "8px" }}>
                <CampaignStatus propertyId={property.id} t={t} />
              </Box>
            </Stack>
          </Box>

          <Box display="flex" justifyContent="space-between">
            {property.propertyStatus !== "Removed" &&
              property.propertyStatus !== "Sold" && (
                <>
                  {!sellerInvited && inviteSellerEnabled && (
                    <Popover
                      trigger={
                        <Button
                          logLabel="send-invite-button"
                          variant="primary"
                          style={{ marginLeft: "16px" }}
                          onClick={() => setInviteSellerModal(true)}
                        >
                          {t("seller.choice.inviteSeller.label")}
                        </Button>
                      }
                    >
                      {t("seller.choice.inviteSeller.description")}
                    </Popover>
                  )}
                  {sellerInvited && inviteSellerEnabled && (
                    <Popover
                      trigger={
                        <CheckMark
                          style={{ marginLeft: "16px" }}
                          width={24}
                          color="#333"
                        />
                      }
                    >
                      {t("seller.choice.inviteSeller.success")}
                    </Popover>
                  )}
                </>
              )}

            {showSeeCampaign ? (
              <Link
                to={`/customer/hq/${property.hq.id}/view/${property.id}`}
                style={{ marginLeft: "16px" }}
              >
                <Button
                  variant="primary"
                  data-testid={property.id}
                  logLabel="see-active-campaign-button"
                >
                  {t("listings.page.see.active.campaign.button.label")}
                </Button>
              </Link>
            ) : (
              <>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Button
                    style={{ marginLeft: "16px" }}
                    logLabel="go-to-order-button"
                    variant="primary"
                    data-testid={property.id}
                    onClick={() =>
                      navigate(
                        getProgramSelectorUrl(
                          property.id,
                          "order/setup",
                          property.hq.id
                        )
                      )
                    }
                  >
                    {t("listings.property.actions.order")}
                  </Button>
                </Box>
              </>
            )}

            <ViewResultsButton hqId={property.hq.id} propertyId={property.id} />
          </Box>
        </Stack>
      </Card>
      {data?.hasSellers ? (
        <InviteSellerModal
          inviteSellerModal={inviteSellerModal}
          setInviteSellerModal={setInviteSellerModal}
          inviteSeller={mutate}
          loading={isLoading}
        />
      ) : (
        <InviteSellerModalWithoutCDP
          property={property}
          inviteSellerModal={inviteSellerModal}
          setInviteSellerModal={setInviteSellerModal}
        />
      )}
    </>
  );
};
